import { ChangeDetectionStrategy, Component, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ConversationChannel,
  MessageType,
  Participant,
  ParticipantType,
  PlatformLocation,
} from '@vendasta/conversation';
import { filterNullAndUndefined } from '@vendasta/rx-utils';
import { Observable, combineLatest, firstValueFrom, map } from 'rxjs';
import { toFirestoreId } from '../../../../core/src/lib/conversation-utils';
import { ConversationService } from '../../../../core/src/lib/conversation.service';
import { InboxService } from '../../../../core/src/lib/inbox.service';
import { ConversationAvailableChannels, MessageInfo } from '../../../../core/src/lib/interface/conversation.interface';
import { PARTNER_ID_TOKEN, USER_ID_TOKEN } from '../../../../core/src/lib/tokens';
import { InboxMessageTextComponent } from '../inbox-chat/components/inbox-message-text/inbox-message-text.component';

@Component({
  selector: 'inbox-ai-chat',
  templateUrl: './inbox-ai-chat.component.html',
  styleUrls: ['./inbox-ai-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InboxAiChatComponent implements OnInit {
  messages$ = this.conversationService.messages$;
  readonly loadingMessages = toSignal(this.conversationService.loadingMessages$);
  readonly currentConversationDetail$ = this.conversationService.currentConversationDetail$;
  readonly loadingConversation = signal(true);
  readonly loading = computed(() => this.loadingConversation() || this.loadingMessages());

  @ViewChild('inboxTextComponent') inboxTextComponent?: InboxMessageTextComponent;

  readonly isImpersonating$: Observable<boolean> = this.conversationService.isUserImpersonated$;

  AIParticipantRecipient = {
    participantType: ParticipantType.PARTICIPANT_TYPE_OPENAI_BOT,
    partnerId: '',
  } as Participant;

  private readonly userId$ = inject(USER_ID_TOKEN);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);

  senderParticipant$: Observable<Participant> = combineLatest([this.userId$, this.partnerId$]).pipe(
    map(([user, partnerID]) => {
      return {
        internalParticipantId: user,
        partnerId: partnerID,
        participantType: ParticipantType.PARTICIPANT_TYPE_IAM_USER,
        isSubjectParticipant: true,
      } as Participant;
    }),
  );

  availableChannels: ConversationAvailableChannels = {
    availableChannels: [ConversationChannel.CONVERSATION_CHANNEL_OPENAI],
    preferredChannel: ConversationChannel.CONVERSATION_CHANNEL_OPENAI,
  };

  constructor(
    private readonly conversationService: ConversationService,
    private readonly inboxService: InboxService,
  ) {}

  ngOnInit(): void {
    this.startConversation();
  }

  async startConversation(): Promise<void> {
    const participants = [await firstValueFrom(this.senderParticipant$), this.AIParticipantRecipient];

    try {
      const resp = await firstValueFrom(
        this.conversationService.createConversation(
          participants ?? [],
          ConversationChannel.CONVERSATION_CHANNEL_OPENAI,
          PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER,
        ),
      );

      this.conversationService.setCurrentFirestoreConversationId(
        toFirestoreId(resp?.conversation?.conversationId ?? ''),
      );
    } catch (error) {
      console.warn('Error to get or create the AI conversation: ', error);
    } finally {
      this.loadingConversation.set(false);
    }
  }

  async sendMessage(messageInfo: MessageInfo): Promise<void> {
    const currentConversationDetail = await firstValueFrom(
      this.currentConversationDetail$.pipe(filterNullAndUndefined()),
    );
    if (currentConversationDetail.conversation.channel !== ConversationChannel.CONVERSATION_CHANNEL_OPENAI) {
      // Just in case the first non-null conversation we load isn't an AI conversation
      // we don't want messages intended for AI appearing in other conversations
      return;
    }
    await this.conversationService.sendMessage(
      currentConversationDetail.conversation.conversationId,
      MessageType.MESSAGE_TYPE_MESSAGE,
      messageInfo.text,
      messageInfo.channel,
      this.inboxService.platformLocation,
      messageInfo.attachments,
    );
    this.inboxTextComponent?.clearText?.();
  }
}
